import { Button, message } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import * as moment from 'moment';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import CommonTable, { ICommonTable } from 'src/components/common/CommonTable';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import I18N from 'src/services/I18NService';
import PriceChangeService, { EnumApproveStatus, IPriceChangeItem, EnumPriceType } from '../../../services/PriceChangeService';
import { MallMedicineType } from 'src/services/MallService';
import { MddCurrency } from '@macrocura/utils';

interface IPriceChangeDetailPageProps extends RouteComponentProps {
  userInfo: IAgentInfo | IDrugStoreInfo;
}

interface IPriceChangeDetailPageState extends ICommonTable<IPriceChangeItem> {
  description: string;
}

class PriceChangeDetailPage extends React.Component<IPriceChangeDetailPageProps, IPriceChangeDetailPageState> {
  constructor(props: IPriceChangeDetailPageProps) {
    super(props);

    const currencySymbol = MddCurrency.getCurrencySymbol(this.props.userInfo.currency);
    const columns: Array<ColumnProps<IPriceChangeItem>> = [
      {
        title: I18N.get('common_rowNum'),
        key: 'index',
        dataIndex: 'index',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          return index + 1;
        },
      },
      {
        title: I18N.get('med_spu_name'),
        key: 'spu',
        dataIndex: 'medicine.spu.name',
      },
      {
        title: I18N.get('med_agent_name'),
        key: 'name',
        dataIndex: 'medicine.name',
      },
      {
        title: I18N.get('med_agent_code'),
        key: 'code',
        dataIndex: 'medicine.code',
      },
      {
        title: I18N.get('common_pharmacy'),
        key: 'medicine_group',
        dataIndex: 'medicine.medicine_group.name',
      },
      {
        title: I18N.get('pharmacy_dosage_form'),
        key: 'dosage_form',
        dataIndex: 'medicine.dosage_form',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          const obj = MallMedicineType.find(t => t.value === record.medicine.dosage_form);
          return !!obj ? I18N.get(obj.key) : '';
        },
      },
      {
        title: I18N.get('common_specification'),
        key: 'specification',
        dataIndex: 'specification',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          return `${record.medicine.specification}${record.medicine.take_unit}/${record.medicine.retail_unit}`;
        },
      },
      {
        title: `${I18N.get('med_change_original_supply_price')}(${currencySymbol})`,
        key: 'original_supply_price',
        dataIndex: 'original_supply_price',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          return this.getOldPrice(record, EnumPriceType.SUPPLY_PRICE)
        },
      },
      {
        title: `${I18N.get('med_change_supply_price')}(${currencySymbol})`,
        key: 'new_supply_price',
        dataIndex: 'new_supply_price',
        className: 'new-price',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          return this.getNewPrice(record, EnumPriceType.SUPPLY_PRICE)
        },
      },
      {
        title: `${I18N.get('med_change_original_floor_price')}(${currencySymbol})`,
        key: 'original_floor_price',
        dataIndex: 'original_floor_price',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          return this.getOldPrice(record, EnumPriceType.SUPPLY_PRICE)
        },
      },
      {
        title: `${I18N.get('med_change_buy_floor_price')}(${currencySymbol})`,
        key: 'new_floor_price',
        dataIndex: 'new_floor_price',
        className: 'new-price',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          return this.getNewPrice(record, EnumPriceType.FLOOR_PRICE)
        },
      },
      {
        title: I18N.get('common_status'),
        key: 'status',
        dataIndex: 'status',
        render: (text: any, record: IPriceChangeItem, index: number): React.ReactNode => {
          const desc = PriceChangeService.getApproveStatusDesc(record.status);
          return record.status === EnumApproveStatus.APPROVE_PASSED ? (
            <span style={{ color: '#00CB4A' }}>{desc}</span>
          ) : record.status === EnumApproveStatus.APPROVE_REJECTED ? (
            <span style={{ color: '#FF0900' }}>{desc}</span>
          ) : (
                desc
              );
        },
      },
    ];

    this.state = {
      rowKey: 'uuid',
      loadingTable: false,
      columns,
      dataSource: [],
      description: '',
    } as IPriceChangeDetailPageState;
  }

  async componentDidMount() {
    await this.getPriceChangeData();
  }

  componentWillUnmount() {
    this.setState = () => {
      return;
    };
  }

  getNewPrice = (record: IPriceChangeItem, type: EnumPriceType): string => {
    let newPrice = '~';
    const detail = (record.details || []).find(item => item.price_type === type);
    if (!!detail) {
      newPrice = detail.new_price.toString();
    }
    return newPrice;
  }

  getOldPrice = (record: IPriceChangeItem, type: EnumPriceType): string => {
    let oldPrice = '';
    const detail = (record.details || []).find(item => item.price_type === type);
    if (!!detail) {
      oldPrice = (detail.original_price || '').toString();
    } else {
      oldPrice = type === EnumPriceType.FLOOR_PRICE ? (record.medicine.floor_price || '').toString()
        : (record.medicine.supply_price || '').toString();
    }
    return oldPrice;
  }

  isChangePrice(record: IPriceChangeItem, type: EnumPriceType): boolean {
    let isChange = false;
    const detail = (record.details || []).find(item => item.price_type === type);
    if (!!detail) {
      isChange = !!detail.new_price;
    }
    return isChange;
  }

  goback = () => {
    this.props.history.goBack();
  };

  getPriceChangeData = async () => {
    if (this.state.loadingTable) {
      return;
    }
    try {
      this.setState({
        loadingTable: true,
      });
      const params = this.props.match.params as { id: string };
      const res = await PriceChangeService.getPriceChangeDataByUUID(params.id);
      if (!!res) {
        if (res.item_uuid_list.length > 0) {
          const items = await PriceChangeService.getPriceChangeDataByUUIDS(res.item_uuid_list);
          if (items.length > 0) {
            this.setState({
              dataSource: items,
              loadingTable: false,
              description: `${moment(res.application_date).format('YYYY-MM-DD HH:mm:ss')},${res.applicant_name}${I18N.get('med_price_change_content_desc')}`,
            });
          }
        }
      }
    } catch (error) {
      this.setState({
        loadingTable: false,
      });
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_search_failure'));
      }
    }
  };

  render() {
    return (
      <div>
        <div className="price-change-page">
          <div style={{ color: '#1FB8CD' }}>{I18N.get('med_price_change_record')}</div>
          <div>
            <Button type="primary" onClick={this.goback}>
              {I18N.get('common_back')}
            </Button>
          </div>
        </div>

        <div className="price-change-page">
          <div>{this.state.description}</div>
        </div>

        <CommonTable rowKey={this.state.rowKey} columns={this.state.columns} loadingTable={this.state.loadingTable} dataSource={this.state.dataSource} />
      </div>
    );
  }
}

export default PriceChangeDetailPage;



// WEBPACK FOOTER //
// ./src/components/views/Price/PriceChangeDetailListPage.tsx